import React, { useCallback, useState, useEffect } from "react";
import useSWR from "swr";
import { useNavigate } from "react-router-dom";
import io from "socket.io-client";

import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import RSelect from "react-select";
import { useDropzone } from "react-dropzone";
import TextField from "@mui/material/TextField";
import SuperAdminLayout from "../../layouts/SuperAdminLayout";
import {
  getCitiesAPI,
  getZonesAPI,
  getStatesAPI,
} from "../../apis/location.apis";
import {
  addVideosAPI,
  uploadZipVideosAPI,
  uploadDriveFolderAPI,
} from "../../apis/videos.apis";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import UploadProgress from "./UploadProgress";
import base_url from "../../constants/base_url";
import * as helpers from "../../utils/helper.utils";

const defaultStateOptions = [{ value: 0, label: "Please Select Zone" }];

const defaultCityOptions = [{ value: 0, label: "Please Select State" }];

// const newRoomId = helpers.generateRandomUniqueString();

export default function AddVideo() {
  const [isUploading, setisUploading] = useState(false);
  const [selectedData, setselectedData] = useState({
    zone_id: null,
    state_id: null,
    city_id: null,
    file: null,
    txtFile: null,
    location_source: "embedded",
  });
  const [roomId, setRoomId] = useState(null);

  const [progressState, setprogressState] = useState({
    message: "Uploading Video..",
    progress: -1,
    overall: -1,
    filename: "",
  });

  useEffect(() => {
    // Check if an upload was already in progress when the page loaded
    const existingRoomId = sessionStorage.getItem("upload_room_id");
    if (existingRoomId) {
      console.log("Found existing upload room. Resuming progress view.");
      setisUploading(true); // Show the UploadProgress component
    }
  }, []);

  const handleBulkUpload = async () => {
    const isDriveUpload = !!selectedData.drive_folder_url;

    if (!isDriveUpload && !selectedData.zip) {
      toast.error("Please provide either a ZIP file or Drive folder link.");
      return;
    }

    if (
      !selectedData.zone_id ||
      !selectedData.state_id ||
      !selectedData.city_id
    ) {
      toast.error("Please select Zone, State, and City.");
      return;
    }

    const formData = new FormData();
    if (selectedData.zip && !isDriveUpload) {
      formData.append("zip_file", selectedData.zip);
    } else if (isDriveUpload) {
      formData.append("drive_folder_url", selectedData.drive_folder_url);
    }
    const newId = helpers.generateRandomUniqueString();
    setRoomId(newId);
    sessionStorage.setItem("upload_room_id", newId);
    formData.append("zone_id", selectedData.zone_id);
    formData.append("state_id", selectedData.state_id);
    formData.append("city_id", selectedData.city_id);
    formData.append("room_id", newId);
  
    try {
      setisUploading(true);
      setprogressState({
        progress: 0,
        message: isDriveUpload
          ? "Downloading from Drive and processing..."
          : "Uploading and processing ZIP...",
      });
      const data = isDriveUpload
        ? await uploadDriveFolderAPI(formData)
        : await uploadZipVideosAPI(formData);
    } catch (error) {

      const isTimeoutOrNetworkError = !error.response;

      if (isTimeoutOrNetworkError) {
  
        toast.warn(
          "Connection was interrupted. Continuing to monitor progress..."
        );
        setprogressState((prev) => ({
          ...prev,
          message: "Connection lost, monitoring background process...",
        }));

      } else {

        const msg =
          error?.response?.data?.message || "Upload failed. Please try again.";
        toast.error(msg);
        setprogressState({ progress: -1 });
        setisUploading(false);
      }
      console.error("Upload Error:", error);
    }
  };

  const citiesDataResp = useSWR(
    selectedData.state_id
      ? "/location/cities?state_id=" + selectedData.state_id
      : null,
    getCitiesAPI.bind(this, selectedData.state_id)
  );

  const zoneDataResp = useSWR("/location/zones", getZonesAPI);

  const statesDataResp = useSWR(
    selectedData.zone_id
      ? "/location/states?zone_id=" + selectedData.zone_id
      : null,
    getStatesAPI.bind(this, selectedData.zone_id)
  );

  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (!file) {
      return;
    }

    setselectedData((prev) => ({ ...prev, file }));
  }, []);

  // Dropzone for video upload
  const {
    getRootProps: getVideoRootProps,
    getInputProps: getVideoInputProps,
    isDragActive: isVideoDragActive,
  } = useDropzone({
    onDrop: useCallback((acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setselectedData((prev) => ({ ...prev, file }));
      }
    }, []),
    maxFiles: 1,
    accept: {
      "video/*": [".mp4"],
    },
  });

  const {
    getRootProps: getTxtRootProps,
    getInputProps: getTxtInputProps,
    isDragActive: isTxtDragActive,
  } = useDropzone({
    onDrop: useCallback((acceptedFiles) => {
      const txtFile = acceptedFiles[0];
      if (txtFile && txtFile.name.endsWith(".txt")) {
        setselectedData((prev) => ({ ...prev, txtFile }));
      }
    }, []),
    maxFiles: 1,
    accept: {
      "text/plain": [".txt"],
    },
  });

  // Dropzone for ZIP upload
  const {
    getRootProps: getZipRootProps,
    getInputProps: getZipInputProps,
    isDragActive: isZipDragActive,
  } = useDropzone({
    onDrop: useCallback((acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file && file.name.endsWith(".zip")) {
        setselectedData((prev) => ({ ...prev, zip: file }));
      }
    }, []),
    maxFiles: 1,
    accept: {
      "application/zip": [".zip"],
    },
  });

  const handleSelectChange = (key = "", d = {}) => {
    setselectedData((prev) => ({ ...prev, [key]: d[key] }));
  };

  const zoneOptions = zoneDataResp.data
    ? zoneDataResp.data.map((v) => ({
        ...v,
        label: v.zone_name,
        value: v.zone_id,
      }))
    : [];

  const stateOptions = statesDataResp.data
    ? statesDataResp.data.map((v) => ({
        ...v,
        label: v.state_name,
        value: v.state_id,
      }))
    : defaultStateOptions;

  const cityOptions = citiesDataResp.data
    ? citiesDataResp.data.map((v) => ({
        ...v,
        label: v.city_name,
        value: v.city_id,
      }))
    : defaultCityOptions;

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);

    if (!selectedData.file) {
      alert("Please select a video file.");
      return;
    }

    data.append("video", selectedData.file);
    data.append("zone_id", selectedData.zone_id);
    data.append("state_id", selectedData.state_id);
    data.append("city_id", selectedData.city_id);
    data.append("room_id", roomId);
    if (selectedData.txtFile) {
      data.append("latlong_txt_file", selectedData.txtFile);
    }
    data.append("location_source", selectedData.location_source);

    const uploadVideo = async (overwrite = 0) => {
      try {
        setisUploading(true);
        const resp = await addVideosAPI(data, overwrite);

        const video_id = resp.video_details.video_id;
        toast.success("Video uploaded successfully!");
        setprogressState({ progress: -1 });
        navigate(`/add-video/${video_id}/processed-output`);
      } catch (e) {
        const msg = e?.response?.data?.message || "Something went wrong!";
        const statusCode = e?.response?.status;

        if (statusCode === 409) {
          const doOverwrite = window.confirm(
            "File for the city already exists! Would you like to overwrite?"
          );

          return doOverwrite;
        }

        setprogressState({ progress: -1 });
        toast.error(msg);
      } finally {
        setisUploading(false);
      }
    };

    uploadVideo().then((doOverwrite) => {
      if (doOverwrite) {
        return uploadVideo(1);
      }
    });
  };

  useEffect(() => {
    const socketInstance = io(base_url);

    socketInstance.on("connect", () => {
      const newRoomId = sessionStorage.getItem("upload_room_id");
      if (newRoomId) {
        socketInstance.emit("join", { room:newRoomId });
      }
      console.log("Connected to server");
    });

    socketInstance.on("processing_progress", (data) => {
      setprogressState((prev) => ({
        ...prev,
        progress: data.percentage,
        message: `Processing video ${data.current_file || "1"} of ${
          data.total_files || "1"
        }: ${data.message}`,
        filename: data.filename || prev.filename,
        overall: Math.floor(
          ((data.current_file || 1) / (data.total_files || 1)) * 100
        ),
      }));
    });

      socketInstance.on("bulk_upload_finished", (data) => {
      toast.success("All videos have been processed!");

      const failedVideosKey = `failed_videos_${data.room_id}`;
      const queueKey = `bulk_review_queue_${data.room_id}`;

      // This is the success logic, now handled by the WebSocket event
      sessionStorage.setItem(queueKey, JSON.stringify(data.video_ids || []));
      sessionStorage.setItem(failedVideosKey, JSON.stringify(data.failed_videos || []));
      sessionStorage.removeItem("upload_room_id"); // Clean up

      setisUploading(false);
      navigate(`/bulk-review/${data.room_id}?index=0`);
    });
    return () => {
      socketInstance.disconnect();
    };
  }, [navigate]);

  const isSingleUploadValid =
    selectedData.file &&
    selectedData.zone_id &&
    selectedData.state_id &&
    selectedData.city_id &&
    (selectedData.location_source === "embedded" ||
      (selectedData.location_source === "text_file" && selectedData.txtFile));

  const isBulkUploadValid =
    (selectedData.zip || selectedData.drive_folder_url) &&
    selectedData.zone_id &&
    selectedData.state_id &&
    selectedData.city_id;

  return (
    <SuperAdminLayout activeLink="/add-video">
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography component="h1" variant="h5">
            Add Video
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <RSelect
                  isLoading={zoneDataResp.isLoading}
                  placeholder="Select Zone"
                  options={zoneOptions}
                  onChange={handleSelectChange.bind(this, "zone_id")}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RSelect
                  isLoading={statesDataResp.isLoading}
                  placeholder="Select State"
                  options={stateOptions}
                  onChange={handleSelectChange.bind(this, "state_id")}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RSelect
                  isLoading={citiesDataResp.isLoading}
                  placeholder="Select City"
                  options={cityOptions}
                  onChange={handleSelectChange.bind(this, "city_id")}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <RSelect
                  placeholder="Select Location Source"
                  value={{
                    value: selectedData.location_source,
                    label:
                      selectedData.location_source === "text_file"
                        ? "Text file"
                        : "Embedded Lat/Long",
                  }}
                  options={[
                    { value: "embedded", label: "Embedded Lat/Long" },
                    { value: "text_file", label: "Text file" },
                  ]}
                  onChange={(e) =>
                    setselectedData((prev) => ({
                      ...prev,
                      location_source: e.value,
                    }))
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <div
                  style={{
                    border: "1px dashed #333",
                    padding: "20px",
                    borderRadius: "15px",
                  }}
                  {...getVideoRootProps()}
                >
                  <input {...getVideoInputProps()} />
                  {isVideoDragActive ? (
                    <p>Drop the files here ...</p>
                  ) : (
                    <p>
                      {selectedData.file
                        ? "Selected File: " + selectedData.file.name
                        : " Drag 'n' drop video file here, or click to select video file"}
                    </p>
                  )}
                </div>
              </Grid>
              {selectedData.location_source === "text_file" && (
                <Grid item xs={12}>
                  <div
                    style={{
                      border: "1px dashed #333",
                      padding: "20px",
                      borderRadius: "15px",
                      marginTop: "10px",
                    }}
                    {...getTxtRootProps()}
                  >
                    <input {...getTxtInputProps()} />
                    <p>
                      {selectedData.txtFile
                        ? `Selected TXT: ${selectedData.txtFile.name}`
                        : "Drop or select .txt file with lat/long"}
                    </p>
                  </div>
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} mt={2}>
              <TextField
                fullWidth
                label="Google Drive Folder Link"
                variant="outlined"
                value={selectedData.drive_folder_url || ""}
                helperText="Paste a folder link containing .mp4 files"
                onChange={(e) =>
                  setselectedData((prev) => ({
                    ...prev,
                    drive_folder_url: e.target.value,
                    zip: null,
                  }))
                }
              />
            </Grid>

            <Grid item xs={12}>
              <div
                style={{
                  border: "1px dashed #333",
                  padding: "20px",
                  borderRadius: "15px",
                  marginTop: "20px",
                }}
                {...getZipRootProps({ multiple: false, accept: ".zip" })}
              >
                <input
                  {...getZipInputProps({
                    accept: ".zip",
                    onChange: (e) => {
                      const file = e.target.files[0];
                      if (file && file.name.endsWith(".zip")) {
                        setselectedData((prev) => ({ ...prev, zip: file }));
                      }
                    },
                  })}
                />
                <p>
                  {selectedData.zip
                    ? "Selected ZIP: " + selectedData.zip.name
                    : "Drop or select a ZIP file for bulk upload"}
                </p>
              </div>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isUploading || !isSingleUploadValid}
              sx={{ mt: 3, mb: 2 }}
            >
              Add
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
              disabled={isUploading || !isBulkUploadValid}
              onClick={handleBulkUpload}
            >
              Add Bulk
            </Button>
          </Box>
        </Box>
        {isUploading ? (
          <UploadProgress
            isLoading={isUploading}
            progress={progressState.progress}
            message={progressState.message}
           roomId={roomId}
            overallProgress={progressState.overall}
            currentFilename={progressState.filename}
          />
        ) : null}
      </Container>
    </SuperAdminLayout>
  );
}

// const handleBulkUpload = async () => {
//   if (!selectedData.zip) {
//     toast.error("Please select a ZIP file for bulk upload.");
//     return;
//   }

//   if (
//     !selectedData.zone_id ||
//     !selectedData.state_id ||
//     !selectedData.city_id
//   ) {
//     toast.error("Please select Zone, State, and City.");
//     return;
//   }

//   const formData = new FormData();
//   formData.append("zip_file", selectedData.zip);
//   formData.append("zone_id", selectedData.zone_id);
//   formData.append("state_id", selectedData.state_id);
//   formData.append("city_id", selectedData.city_id);
//   formData.append("room_id",newRoomId);

//   try {
//     setisUploading(true);
//     setprogressState({
//       progress: 0,
//       message: "Uploading and processing ZIP...",
//     });

//     const data = await uploadZipVideosAPI(formData);

//     const failedVideosKey = `failed_videos_${roomId}`;
//     const queueKey = `bulk_review_queue_${roomId}`;
//     sessionStorage.setItem(queueKey, JSON.stringify(data.video_ids));
//     sessionStorage.setItem(
//       failedVideosKey,
//       JSON.stringify(data.failed_videos || [])
//     );
//     console.log("Bulk Upload Response:", data);

//     // Start review with the first video:
//     navigate(`/bulk-review/${roomId}?index=0`);

//     toast.success("ZIP uploaded! Processing started.");
//   } catch (error) {
//     const msg =
//       error?.response?.data?.message ||
//       "Bulk upload failed. Please try again.";
//     toast.error(msg);
//     setprogressState({ progress: -1 });
//     console.error("Bulk Upload Error:", error);
//   } finally {
//     setisUploading(false);
//   }
// };
